import { Component, EventEmitter, Input, Output, OnInit, ContentChild, TemplateRef, ChangeDetectorRef, ViewChild } from '@angular/core';
import { DateRangeTerm, DateRange } from './range-picker.models';
import { FormControl } from '@angular/forms';
import { SearchRangeTerms } from '../../models/search-range-terms.models';
import { DatePicker } from 'primeng/datepicker';

@Component({
  selector: 'shared-ui-datetime-range-picker',
  templateUrl: './range-picker.component.html',
  styleUrls: ['./range-picker.component.css'],
  standalone: false,
})
export class RangePickerComponent implements OnInit {
  @ContentChild('footer', { read: TemplateRef }) footerTemplate!: TemplateRef<unknown>;
  @Input() formControlVariableName?: FormControl;
  @Input() formControlSearchTerm?: FormControl;
  @Input() parentMaxDate?: Date;
  @ViewChild('dp', { static: false }) datepicker!: DatePicker;

  @Output() dateFilter = new EventEmitter<DateRangeTerm>();

  rangeDates: Date[] | null = [];

  minDate = new Date(2015, 0, 1);
  maxDate = new Date();

  searchRangeTerms = SearchRangeTerms;
  selectedSearchRangeTerm?: SearchRangeTerms;

  constructor(protected cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.parentMaxDate) {
      this.maxDate = this.parentMaxDate;
    }
  }

  clearFilters(): void {
    this.rangeDates = null;
    this.selectedSearchRangeTerm = undefined;
    if (this.datepicker) {
      this.datepicker.updateInputfield();
    }
    this.cdr.detectChanges();
  }

  updateRange(start: Date, end: Date): void {
    this.rangeDates = [new Date(start.getFullYear(), start.getMonth(), start.getDate()), new Date(end.getFullYear(), end.getMonth(), end.getDate())];
    if (this.datepicker) {
      this.datepicker.updateInputfield();
    }
    this.cdr.detectChanges();
  }

  onRangeDatesChange(): void {
    if (this.rangeDates?.length === 2 && this.rangeDates[0] && this.rangeDates[1]) {
      this.emitSelectedDate();
    }

    this.cdr.detectChanges();
  }

  emitSelectedDate(): void {
    if (!this.rangeDates || this.rangeDates.length < 2 || !this.rangeDates[0] || !this.rangeDates[1]) {
      return;
    }

    const startDate = new Date(`${this.rangeDates[0].getFullYear()}-${this.rangeDates[0].getMonth() + 1}-${this.rangeDates[0].getDate()}`);
    startDate.setHours(0, 0, 0);
    const endDate = new Date(`${this.rangeDates[1].getFullYear()}-${this.rangeDates[1].getMonth() + 1}-${this.rangeDates[1].getDate()}`);
    endDate.setHours(23, 59, 59);

    this.detectSelectedDateRange(startDate, endDate);

    this.dateFilter.emit(new DateRangeTerm(new DateRange(startDate, endDate), this.selectedSearchRangeTerm));

    this.cdr.detectChanges();
  }

  getMaxPossibleDate(): Date {
    const maxPossibleDay = new Date();

    if (!this.parentMaxDate) {
      return maxPossibleDay;
    }

    const lastDayOfMonth = new Date(maxPossibleDay.getFullYear(), maxPossibleDay.getMonth() + 1, 0);

    return lastDayOfMonth;
  }

  detectSelectedDateRange(startDate: Date, endDate: Date): void {
    const today = new Date();

    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const thisMonthStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const thisMonthEndDate = new Date(today.getFullYear(), today.getMonth(), this.getMaxPossibleDate().getDate());

    const lastMonthStartDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastMonthEndDate = new Date(today.getFullYear(), today.getMonth(), 0);

    if (startDate.toDateString() === today.toDateString() && endDate.toDateString() === today.toDateString()) {
      this.selectedSearchRangeTerm = this.searchRangeTerms.Today;
    } else if (startDate.toDateString() === sevenDaysAgo.toDateString() && endDate.toDateString() === today.toDateString()) {
      this.selectedSearchRangeTerm = this.searchRangeTerms.LastSevenDays;
    } else if (startDate.toDateString() === thirtyDaysAgo.toDateString() && endDate.toDateString() === today.toDateString()) {
      this.selectedSearchRangeTerm = this.searchRangeTerms.LastThirtyDays;
    } else if (startDate.toDateString() === thisMonthStartDate.toDateString() && endDate.toDateString() === thisMonthEndDate.toDateString()) {
      this.selectedSearchRangeTerm = this.searchRangeTerms.ThisMonth;
    } else if (startDate.toDateString() === yesterday.toDateString() && endDate.toDateString() === yesterday.toDateString()) {
      this.selectedSearchRangeTerm = this.searchRangeTerms.Yesterday;
    } else if (startDate.toDateString() === lastMonthStartDate.toDateString() && endDate.toDateString() === lastMonthEndDate.toDateString()) {
      this.selectedSearchRangeTerm = this.searchRangeTerms.LastMonth;
    } else {
      this.selectedSearchRangeTerm = this.searchRangeTerms.CustomRange;
    }
  }
}
